import { useMemo, useState } from 'react'
import { MenuItem, Select, Stack } from '@mui/material'
import { yaml } from '@codemirror/lang-yaml'
import { javascript } from '@codemirror/lang-javascript'
import { json } from '@codemirror/lang-json'
import { Extension } from '@codemirror/state'

export type Language = 'plain' | 'yaml' | 'json' | 'javascript'

const languageLabels: Record<Language, string> = {
  plain: 'Plain Text',
  yaml: 'YAML',
  json: 'JSON',
  javascript: 'JavaScript',
}

const languageExtensions: Record<Language, Extension[]> = {
  plain: [],
  yaml: [yaml()],
  json: [json()],
  javascript: [javascript()],
}

export function detectLanguage(content: string | undefined): Language {
  if (!content?.trim()) return 'plain'
  const trimmed = content.trim()

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed)
      return 'json'
    } catch {
      // not valid JSON, could still be JS
    }
  }

  if (/^\s*(function|const|let|var|import|export|class)\s/m.test(trimmed)) return 'javascript'
  if (/^\s*[\w.-]+\s*:/m.test(trimmed) && !trimmed.includes(';')) return 'yaml'

  return 'plain'
}

export function getLanguageExtensions(language: Language): Extension[] {
  return languageExtensions[language]
}

export function useLanguageDetection(content: string | undefined) {
  const [language, setLanguage] = useState<Language>(() => detectLanguage(content))
  const [userOverride, setUserOverride] = useState(false)

  const detected = useMemo(() => detectLanguage(content), [content])
  const activeLanguage = userOverride ? language : detected

  const selectActiveLanguage = (lang: Language) => {
    setLanguage(lang)
    setUserOverride(true)
  }

  return { activeLanguage, selectActiveLanguage }
}

export const LanguageSelect = ({ value, onChange }: { value: Language; onChange: (language: Language) => void }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="flex-end"
    sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', flexShrink: 0 }}
  >
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as Language)}
      variant="standard"
      disableUnderline
      sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0 } }}
    >
      {(Object.keys(languageLabels) as Language[]).map((lang) => (
        <MenuItem key={lang} value={lang} sx={{ fontSize: '0.75rem' }}>
          {languageLabels[lang]}
        </MenuItem>
      ))}
    </Select>
  </Stack>
)
